import React, { useState, useEffect, useRef } from 'react';
import TopBar from '../../Components/Games/TopBar.jsx';
import './FocusFinder.css';
import ResultPage from '../../Components/Games/ResultPage.jsx';
import StartGame from '../../Components/Games/StartGame.jsx';
import overlay from '../../assets/img/overlay.png';
import GameIntroScreen from '../../Components/Games/GameIntroScreen.jsx';
import focusFinderIcon from '../../assets/img/icon-Focus-Finder.png';
import { lifestyleApi } from '../../apiService/api';
import { getUrlParams } from '../../apiService/timespam';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";

const ASSETS = {
    EASY: { pool: ['🍌', '🍇', '🍉', '🍊', '🍍', '🍓', '🍒', '🥝', '🥑', '🌽', '🥕', '🍎', '🍐', '🍋', '🍈', '🍏', '🍑', '🥭', '🥥', '🥦', '🥬', '🥒', '🌶️', '🫑', '🧅', '🧄', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨'] },
    MEDIUM: { pool: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜'] },
    HARD: { pool: ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🎱', '🥏', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '🛷', '⛸️', '🥌', '🎿', '⛷️', '🏂'] },
    EXPERT: { pool: ['🙂', '🙁', '😮', '😯', '😦', '😧', '😩', '😬', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😪', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖'] }
};

const FocusFinder = () => {
    useGameBackNavigation();
    const [screen, setScreen] = useState("intro");
    const [gameState, setGameState] = useState('START');
    const [tutorialStep, setTutorialStep] = useState(0);
    const [level, setLevel] = useState(1);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [gridItems, setGridItems] = useState([]);
    const [currentTarget, setCurrentTarget] = useState('');
    const [gridSize, setGridSize] = useState(3);
    const [streak, setStreak] = useState(0);
    const [shakeIndex, setShakeIndex] = useState(null);
    const [popIndex, setPopIndex] = useState(null);
    const [popupMsg, setPopupMsg] = useState(null);

    // ✅ Best Score Syncing
    const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem('bestFocusScore')) || 0);
    const [bestStreak, setBestStreak] = useState(() => parseInt(localStorage.getItem('bestFocusStreak')) || 0);
    const [cid, setCid] = useState("");
    const [groupName, setGroupName] = useState("");
    const [gameStateApi, setGameStateApi] = useState("initial");
    const [lastInsertedId, setLastInsertedId] = useState(null);
    const [goqiiBest, setGoqiiBest] = useState(0);

    const [correct, setCorrect] = useState(0);
    const [wrong, setWrong] = useState(0);
    const [fastResponses, setFastResponses] = useState(0);
    const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
    const [backendSkill, setBackendSkill] = useState("");
    const [weightages, setWeightages] = useState({ attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1, fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0, correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1 });
    const [configError, setConfigError] = useState(false);


    const timerRef = useRef(null);
    const levelStartTime = useRef(0);
    const msgTimeoutRef = useRef(null);

    const startGame = async (isReplay = false) => {
        const { encryptedId, gid } = getUrlParams();

        try {
            const res = await lifestyleApi.initialGameScore(encryptedId, gid);

            if (res.data?.code === 200) {
                const data = res.data.data;

                setCid(data.cid);
                setGroupName(data.groupName);
                setLastInsertedId(data.lastInsertedId);
                setGameStateApi("initial");
            }
        } catch (err) {
            console.error("initialGameScore failed", err);
        }


        setScore(0);
        setLevel(1);
        setTimeLeft(60);
        setCorrect(0);
        setWrong(0);
        setStreak(0);
        setFastResponses(0);
        setScreen("game");
        generateLevel(1);

        if (isReplay) {
            setGameState('PLAYING');
            startTimer();
        } else {
            setGameState('TUTORIAL');
            setTutorialStep(0);
            setTimeout(() => {
                setTutorialStep(prev => (prev === 0 ? 1 : prev));
            }, 3500);
        }
    };


    const fetchGameConfig = () => {
        setConfigError(false);
        const { encryptedId, gid } = getUrlParams();

        lifestyleApi.fetchGameDetails(encryptedId, gid)
            .then(res => {
                if (res.data?.code === 200) {
                    const d = res.data.data;
                    setBestScore(d.bestScore);
                    setGoqiiBest(d.goqiiBest);
                    setWeightages({
                        attemptWeightage: d.attemptWeightage ?? 1,
                        accuracyWeightage: d.accuracyWeightage ?? 1,
                        streakWeightage: d.streakWeightage ?? 1,
                        fastWeightage: d.fastWeightage ?? 1,
                        scoreWeightage: d.scoreWeightage ?? 1,
                        baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
                        correctMinQuestion: d.correctMinQuestion || 1,
                        streakMinQuestion: d.streakMinQuestion || 1,
                        fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
                        scorePoints: d.scorePoints || 1,
                    });
                } else {
                    setConfigError(true);
                }
            })
            .catch(err => {
                console.error("fetchGameDetails failed", err);
                setConfigError(true);
            });
    };

    useEffect(() => {
        fetchGameConfig();
    }, []);
    const startTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    setGameState('GAMEOVER');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };


    const generateLevel = (currentLevel) => {
        setShakeIndex(null);
        setPopIndex(null);
        let size = currentLevel > 7 ? 5 : currentLevel > 3 ? 4 : 3;
        let assetKey = currentLevel > 12 ? 'EXPERT' : currentLevel > 7 ? 'HARD' : currentLevel > 3 ? 'MEDIUM' : 'EASY';
        setGridSize(size);
        const config = ASSETS[assetKey];
        const distractorPool = [...config.pool];
        const targetIndex = Math.floor(Math.random() * distractorPool.length);
        const newTarget = distractorPool[targetIndex];
        distractorPool.splice(targetIndex, 1);
        setCurrentTarget(newTarget);
        const totalCells = size * size;
        distractorPool.sort(() => Math.random() - 0.5);
        const uniqueDistractors = distractorPool.slice(0, totalCells - 1);
        let items = [{ id: `t-${Date.now()}`, char: newTarget, isTarget: true }];
        uniqueDistractors.forEach((char, i) => items.push({ id: `d-${i}-${Date.now()}`, char, isTarget: false }));
        items.sort(() => Math.random() - 0.5);
        setGridItems(items);
        levelStartTime.current = Date.now();
    };

    const triggerTargetPopup = (text) => {
        if (msgTimeoutRef.current) clearTimeout(msgTimeoutRef.current);
        setPopupMsg({ text, id: Date.now() });
        msgTimeoutRef.current = setTimeout(() => setPopupMsg(null), 800);
    };

    const handleItemClick = (index, item) => {
        if (gameState === 'TUTORIAL') {
            if (item.isTarget) {
                // ✅ Show green highlight immediately
                setPopIndex(index);
                // Enter "START" transition
                setTimeout(() => {
                    setTutorialStep(-1);
                }, 500);

                setTimeout(() => {
                    setGameState('PLAYING');
                    startTimer();
                    const nextLevel = level + 1;
                    setLevel(nextLevel);
                    generateLevel(nextLevel);
                    setPopIndex(null); // Clear highlight for game start
                }, 1300);
            } else {
                setShakeIndex(index);
                setTimeout(() => setShakeIndex(null), 400);
            }
            return;
        }

        if (gameState !== 'PLAYING') return;

        if (item.isTarget) {
            setPopIndex(index);
            const timeTaken = Date.now() - levelStartTime.current;
            let fastBonus = 0;
            if (timeTaken < 1500) {
                setFastResponses(prev => prev + 1);
                fastBonus = weightages.fastWeightage * 10;
            }
            triggerTargetPopup("CORRECT!");

            // streak here is the streak count BEFORE this correct answer
            const streakBonus = weightages.streakWeightage * 10 * streak;
            const gained = weightages.scorePoints + streakBonus + fastBonus;
            const newScore = Math.max(0, score + gained);
            setScore(newScore);
            setCorrect(prev => prev + 1);

            const newStreak = streak + 1;
            setStreak(newStreak);

            // ✅ BEST SCORE LOGIC
            if (newScore > bestScore) {
                setBestScore(newScore);
                localStorage.setItem('bestFocusScore', newScore.toString());
            }
            if (newStreak > bestStreak) {
                setBestStreak(newStreak);
                localStorage.setItem('bestFocusStreak', newStreak.toString());
            }

            setTimeout(() => {
                const nextLevel = level + 1;
                setLevel(nextLevel);
                generateLevel(nextLevel);
            }, 250);
        } else {
            setShakeIndex(index);
            setStreak(0);
            setWrong(prev => prev + 1);
            setScore(s => Math.max(0, s - weightages.scorePoints / 2));
            setTimeout(() => setShakeIndex(null), 400);
        }
    };

    const skipTutorial = (e) => {
        e.stopPropagation();
        setTutorialStep(-1);
        setTimeout(() => {
            setGameState('PLAYING');
            startTimer();
        }, 1200);
    };

    useEffect(() => { return () => clearInterval(timerRef.current); }, []);

    const saveFinalGameScore = async () => {
        const { encryptedId, gid } = getUrlParams();
        const goqiiUserId = encryptedId;
        const totalQuestions = correct + wrong;

        const payload = {
            goqiiUserId,
            gid,
            totalQuestions,
            correctAnswers: correct,
            wrongAnswers: wrong,
            longestStreak: bestStreak,
            fastResponse: fastResponses,
            score,
            cid,
            lastInsertedId,
        };

        try {
            const res = await lifestyleApi.saveGameScore(
                payload.goqiiUserId,
                payload.gid,
                payload.totalQuestions,
                payload.correctAnswers,
                payload.wrongAnswers,
                payload.longestStreak,
                payload.fastResponse,
                payload.score,
                payload.cid,
                payload.lastInsertedId
            );

            if (res?.data?.data?.cognitiveScore !== undefined) {
                setBackendCognitiveScore(res.data.data.cognitiveScore);
            }

            if (res?.data?.data?.skill) {
                setBackendSkill(res.data.data.skill);
            }
        } catch (err) {
            console.error("saveGameScore failed", err);
        }
    };
    useEffect(() => {
        if (gameState === 'GAMEOVER') {
            saveFinalGameScore();
        }
    }, [gameState]);

    const getQuote = () => {
        const accurate = correct + wrong > 0 ? Math.round((correct / (correct + wrong)) * 100) : 0;

        if (backendCognitiveScore >= 90 || (accurate >= 95 && bestStreak >= 10))
            return "Eagle-eyed focus! You spot targets instantly.";
        if (backendCognitiveScore >= 75 || (accurate >= 85 && bestStreak >= 6))
            return "Sharp vision! Your attention is fast and precise.";
        if (backendCognitiveScore >= 60 || accurate >= 70)
            return "Good scanning skills. Your focus is improving.";
        if (backendCognitiveScore >= 40)
            return "Nice effort. Train more to boost your visual speed.";
        return "Every round trains your brain. Stay alert and try again!";
    };


    return (
        <div className="focus-finder-container">
            {configError && (
                <div className="api-error-overlay">
                    <div className="api-error-card">
                        <div className="api-error-title">Something went wrong</div>
                        <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
                        <button className="api-error-retry-btn" onClick={fetchGameConfig}>Retry</button>
                    </div>
                </div>
            )}

            {screen === "game" && <TopBar score={score} timeLeft={timeLeft} changeDurationMs={700} timerAlertThreshold={10} />}
            <div className="main-area">
                {screen === "intro" ? (
                    <GameIntroScreen backgroundImage={overlay} gameLogo={focusFinderIcon} title="Focus Finder" onStart={() => setScreen("start")} />
                ) : gameState === 'START' ? (
                    <StartGame backgroundImage={overlay}
                        //  hbTitle="Health Benefits" 
                        hbHeading="Selective Attention" hbDescription="Boost attention & visual scanning" yourBest={bestScore} goqiiBest={goqiiBest} onStart={() => startGame(false)} />
                ) : gameState === 'GAMEOVER' ? (
                    <ResultPage visible={true} score={score} best={bestScore} correct={correct} wrong={wrong} longestStreak={bestStreak} fastResponses={fastResponses} cognitiveScore={backendCognitiveScore} domainLabel={backendSkill} quote={getQuote()} onReplay={() => startGame(true)} />
                ) : (
                    <div className="ff-game-board">
                        {gameState === 'TUTORIAL' && (
                            <div className={`ff-tutorial-overlay ${tutorialStep === -1 ? 'starting' : ''}`}>
                                {tutorialStep === -1 && <div className="ff-big-start">START!</div>}
                                {tutorialStep !== -1 && (
                                    <>
                                        <button className="ff-skip-btn" onClick={skipTutorial}>Skip ⏩</button>
                                        {tutorialStep === 0 && (
                                            <div className="ff-guide-step target-guide fade-in-out">
                                                <div className="ff-target-arrow">←</div>
                                                <p>Look at the <strong>Target</strong></p>
                                            </div>
                                        )}

                                        {tutorialStep === 1 && (
                                            <div className="ff-guide-step grid-guide">
                                                <p>Find and Tap it in the <strong>Grid</strong></p>
                                                <div className="ff-guide-arrow">↓</div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        <div className="ff-header">
                            <div className="ff-target-box">
                                {popupMsg && <div key={popupMsg.id} className="ff-popup-target">{popupMsg.text}</div>}
                                <span className="ff-label">FIND THIS</span>
                                <div className="ff-target-icon">{currentTarget}</div>
                            </div>
                            {streak > 1 && <div className="ff-streak-badge">🔥 {streak} Streak</div>}
                        </div>
                        <div className={`ff-grid cols-${gridSize}`}>
                            {gridItems.map((item, index) => (
                                <button key={item.id} className={`ff-cell ${shakeIndex === index ? 'shake' : ''} ${popIndex === index ? 'pop' : ''}`} onClick={() => handleItemClick(index, item)}>
                                    {item.char}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FocusFinder;